import {
  useMutation,
  useQuery,
  useQueryClient,
  QueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import { requestV3 } from 'Features/Auth/axios-client'
import { PreferredMethod } from 'Features/Projects/Scenarios/Templates/PlanTemplateSummary.types'
import {
  MethodComparison,
  TemplateOutputs,
  TemplateSummaryOutputs,
  TemplateOutputsRunRateTable,
} from 'Features/Projects/Scenarios/Templates/TemplateResults/TemplateResults.types'

// The API reads `preferredTemplate`; these requests used to send
// `preferredMethod`, which it ignored, so every method showed the constrained
// template.

const fetchTemplateMethodComparison = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string
) => {
  const { data } = await requestV3({
    url: `/template-method-comparison?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}`,
  })
  return data
}

/** One work centre's templates from every method, for the comparison tab. */
export const useTemplateMethodComparison = ({
  projectId,
  scenarioId,
  workcentreId,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
}): UseQueryResult<MethodComparison> => {
  return useQuery({
    queryKey: [
      'template-method-comparison',
      projectId,
      scenarioId,
      workcentreId,
    ],
    queryFn: () =>
      fetchTemplateMethodComparison(projectId, scenarioId, workcentreId),
    select: (data) => data.data,
    staleTime: 0, // refetch on each visit, but show the cached result meanwhile
  })
}

const fetchTemplateOutputs = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string,
  preferredMethod: PreferredMethod
) => {
  const { data } = await requestV3({
    url: `/template-outputs?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredTemplate=${preferredMethod}`,
  })

  return data
}

export const useTemplateOutputs = ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
}): UseQueryResult<TemplateOutputs[]> => {
  return useQuery({
    queryKey: [
      'template-outputs',
      projectId,
      scenarioId,
      workcentreId,
      preferredMethod,
    ],
    queryFn: () =>
      fetchTemplateOutputs(
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod
      ),
    select: (data) => data.data,
    staleTime: 0, // refetch on each visit, but show the cached result meanwhile
  })
}

const fetchTemplateComparison = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string,
  preferredMethod: PreferredMethod
) => {
  const { data } = await requestV3({
    url: `/template-comparison?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredTemplate=${preferredMethod}`,
  })
  return data
}

export const useTemplateComparison = ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
}) => {
  return useQuery({
    queryKey: [
      'template-comparison',
      projectId,
      scenarioId,
      workcentreId,
      preferredMethod,
    ],
    queryFn: () =>
      fetchTemplateComparison(
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod
      ),
    select: (data) => data.data,
    staleTime: 0, // refetch on each visit, but show the cached result meanwhile
  })
}

const fetchTemplateRunRateOutputs = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string,
  preferredMethod: PreferredMethod
) => {
  const { data } = await requestV3({
    url: `/template-outputs-run-rate?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredTemplate=${preferredMethod}`,
  })

  return data
}

export const useFetchTemplateRunRateOutputs = ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
}): UseQueryResult<TemplateOutputsRunRateTable[]> => {
  return useQuery({
    queryKey: [
      'template-outputs-run-rate',
      projectId,
      scenarioId,
      workcentreId,
      preferredMethod,
    ],
    queryFn: () =>
      fetchTemplateRunRateOutputs(
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod
      ),
    select: (data) => data.data,
    staleTime: 0, // refetch on each visit, but show the cached result meanwhile
  })
}

export type UpdateParams = {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
  updateData: TemplateOutputsRunRateTable[]
}

const updateRunRateTable = async ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
  updateData,
}: UpdateParams) => {
  const { data } = await requestV3({
    url: `/template-outputs-run-rate?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredTemplate=${preferredMethod}`,
    method: 'put',
    data: updateData,
  })

  return data
}

export const useUpdateRunRateTable = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: updateRunRateTable,
    onSuccess: () => {
      invalidateTemplateResults(queryClient)
      onSuccess()
    },
    onError: () => {
      onError()
    },
  })
}

const fetchTemplateSummaryOutputs = async (
  projectId: string,
  scenarioId: string,
  workcentreId: string,
  preferredMethod: PreferredMethod
) => {
  const { data } = await requestV3({
    url: `/template-summary-outputs?projectId=${projectId}&scenarioId=${scenarioId}&workcentreId=${workcentreId}&preferredTemplate=${preferredMethod}`,
  })

  return data
}

export const useFetchTemplateSummaryOutputs = ({
  projectId,
  scenarioId,
  workcentreId,
  preferredMethod,
}: {
  projectId: string
  scenarioId: string
  workcentreId: string
  preferredMethod: PreferredMethod
}): UseQueryResult<TemplateSummaryOutputs[]> => {
  return useQuery({
    queryKey: [
      'template-summary-outputs',
      projectId,
      scenarioId,
      workcentreId,
      preferredMethod,
    ],
    queryFn: () =>
      fetchTemplateSummaryOutputs(
        projectId,
        scenarioId,
        workcentreId,
        preferredMethod
      ),
    select: (data) => data.data,
    enabled: !!projectId && !!scenarioId && !!workcentreId && !!preferredMethod,
    staleTime: 0, // refetch on each visit, but show the cached result meanwhile
  })
}

/**
 * Refetch every query that reads TemplateOutputs or TemplateSummaryOutputs:
 * after a Run rate save rewrites them, or a run replaces a work centre's.
 */
export const invalidateTemplateResults = (queryClient: QueryClient) => {
  for (const key of [
    'template-outputs-run-rate',
    'template-summary-outputs',
    'template-outputs',
    'template-comparison',
    'template-method-comparison',
  ]) {
    queryClient.invalidateQueries({ queryKey: [key] })
  }
}
